import React from 'react';
import { RouteObject, Navigate } from 'react-router-dom';

// Core imports
import { MENU_DATA } from '@core/data/menuData.ts';
import { PageUtils } from '@core/utils/pageUtils.ts';

// Shared components
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';
import { PageAnimationWrapper } from '@/components/shared/navigation/PageAnimationWrapper.tsx';
import ComingSoon from '@/layouts/ComingSoon.tsx';
import NotFound from '@/layouts/NotFound.tsx';

// Route utilities
import ProtectedRoute from '@/route/ProtectedRoute.tsx';

// Dashboard features
import MyShops from '@features/shop-control-center/MyShops.tsx';

// Monitoring features
import TrolleyShelterMonitoring from '@features/monitoring/TrolleyShelterMonitoring.tsx';
import HybridLocBatteryMonitoring from '@features/monitoring/HybridLocBatteryMonitoring.tsx';
import FastLanerHealthMonitoring from '@features/monitoring/FastLanerHealthMonitoring.tsx';
import FastLanerOverview from '@features/monitoring/FastLanerOverview.tsx';
import TrolleyShelterOverview from '@features/monitoring/TrolleyShelterOverview.tsx';

// Administration features
import DataExports from '@features/administration/DataExports.tsx';
import Info from '@features/administration/Info.tsx';
import MediaPlayerTemplates from '@features/administration/MediaPlayerTemplates';
import FastLaner from '@features/administration/FastLaner.tsx';
import RfidTags from '@features/administration/RfidTags.tsx';
import Products from '@features/administration/Products.tsx';
import OperationModes from '@features/administration/OperationModes.tsx';
import OperationModesSchedules from '@features/administration/OperationModesSchedules.tsx';
import AuthenticationProviders from '@features/administration/AuthenticationProviders.tsx';
import RecycleBin from '@features/administration/RecycleBin.tsx';
import Modules from '@features/administration/components/recyclebin/Modules.tsx';
import DesignElements from '@features/administration/DesignElements.tsx';
import LogicServiceManagement from '@features/administration/LogicServiceManagement.tsx';

// Shop Automation features
import ShopAutomationIncidents from '@features/shopautomation/Incidents.tsx';
import ShopAutomationShops from '@features/shopautomation/ShopAutomationShops.tsx';

// Statistics - HybridLoc
import HybridLocDailyUsage from '@features/statistics/HybridLocDailyUsage.tsx';
import HybridLocDetailedUsage from '@features/statistics/HybridLocDetailedUsage.tsx';
import HybridLocDistributionUsage from '@features/statistics/HybridLocDistributionUsage.tsx';

// Statistics - Gates
import GatesInsights from '@features/statistics/GatesInsights.tsx';
import GatesOpenings from '@features/statistics/GatesOpenings.tsx';
import GatesDetails from '@features/statistics/GatesDetails.tsx';

// Statistics - TrolleyShelter
import TrolleyShelterDailyDetails from '@features/statistics/TrolleyShelterDailyDetails.tsx';
import TrolleyShelterDailyComparison from '@features/statistics/TrolleyShelterDailyComparison.tsx';

// Statistics - ZoneTracker
import ZoneTrackerDailyDetails from '@features/statistics/ZoneTrackerDailyDetails.tsx';
import ZoneTrackerDailyComparison from '@features/statistics/ZoneTrackerDailyComparison.tsx';
import ZoneTrackerEntries from '@features/statistics/ZoneTrackerEntries.tsx';
import ZoneTrackerDwellTime from '@features/statistics/ZoneTrackerDwellTime.tsx';
import ZoneTrackerOverview from '@features/statistics/ZoneTrackerOverview.tsx';

// Statistics - LineTracker
import LineTrackerRelations from '@features/statistics/LineTrackerRelations.tsx';

// User Management
import Users from '@features/user-management/Users.tsx';
import SuperAdmins from '@features/user-management/SuperAdmins.tsx';
import Organisations from '@features/user-management/Organisations.tsx';
import { ActiveLicenses, Role } from '@/core/types';
import NotificationTemplates from '@features/administration/NotificationTemplates.tsx';
import { ShopSetup } from '@features/administration';

// Helper function to wrap components with ProtectedRoute
const protectedRoute = (
    PageComponent: FCSecurePage,
    routePath: string,
    requiredRole?: Role,
    requiredLicense?: ActiveLicenses | ActiveLicenses[]
) => (
    <ProtectedRoute requiredRole={requiredRole} requiredLicense={requiredLicense}>
        <PageAnimationWrapper animationType="fade" duration={600} key={routePath}>
            <PageComponent />
        </PageAnimationWrapper>
    </ProtectedRoute>
);

// Map of implemented components by path
const IMPLEMENTED_COMPONENTS: { [path: string]: FCSecurePage } = {
    '/myshops': MyShops,

    '/manage-shop/shopsetup': ShopSetup,
    '/manage-shop/remote-control': ShopAutomationShops,
    '/manage-shop/operationModesSchedules': OperationModesSchedules,
    '/manage-shop/recyclebin': RecycleBin,
    '/manage-shop/recyclebin/:id/modules': Modules,

    '/statistics/events': ShopAutomationIncidents,

    '/statistics/fastLaner/health': FastLanerHealthMonitoring,
    '/statistics/fastLaner/overview': FastLanerOverview,

    '/statistics/rfid/overview': TrolleyShelterOverview,
    '/statistics/rfid/dailydetails': TrolleyShelterDailyDetails,
    '/statistics/rfid/dailycomparison': TrolleyShelterDailyComparison,
    '/statistics/rfid/monitoring': TrolleyShelterMonitoring,

    '/statistics/zonetracker/dailydetails': ZoneTrackerDailyDetails,
    '/statistics/zonetracker/dailycomparison': ZoneTrackerDailyComparison,
    '/statistics/zonetracker/entries': ZoneTrackerEntries,
    '/statistics/zonetracker/dwelltime': ZoneTrackerDwellTime,
    '/statistics/zonetracker/overview': ZoneTrackerOverview,

    '/statistics/gates/insights': GatesInsights,
    '/statistics/gates/openings': GatesOpenings,
    '/statistics/gates/details': GatesDetails,

    '/statistics/hybridloc/daily-usage': HybridLocDailyUsage,
    '/statistics/hybridloc/detailed-usage': HybridLocDetailedUsage,
    '/statistics/hybridloc/appcoin-usage': HybridLocDistributionUsage,
    '/statistics/hybridloc/battery': HybridLocBatteryMonitoring,

    '/statistics/linetracker/relations': LineTrackerRelations,

    '/statistics/dataexports': DataExports,

    '/manage-organisation/organisations': Organisations,
    '/manage-organisation/mediaPlayerTemplates': MediaPlayerTemplates,
    '/manage-organisation/info': Info,
    '/manage-organisation/notificationtemplates': NotificationTemplates,
    '/manage-organisation/fastLanerSettings': FastLaner,
    '/manage-organisation/rfidtags': RfidTags,
    '/manage-organisation/products': Products,
    '/manage-organisation/operationModes': OperationModes,
    '/manage-organisation/authenticationProviders': AuthenticationProviders,
    '/manage-organisation/logic-service-management': LogicServiceManagement,
    '/manage-organisation/design-elements': DesignElements,

    '/manage-users/users': Users,
    '/manage-users/superAdmins': SuperAdmins
};

// Function to recursively generate routes from menu items
const generateRoutesFromMenuItems = (items: any[]): RouteObject[] => {
    const routes: RouteObject[] = [];

    for (const item of items) {
        if (item.path) {
            const implementedComponent = IMPLEMENTED_COMPONENTS[item.path];
            let componentName = implementedComponent ? implementedComponent?.displayName : 'ComingSoon';
            if (componentName === undefined) {
                componentName = PageUtils.getPageName(implementedComponent);
            }

            const component = implementedComponent || ComingSoon;
            const element = protectedRoute(component, item.path, item.role, item.license);

            routes.push({
                path: item.path,
                element: element,
                handle: { name: componentName }
            });
        }

        if (item.items && item.items.length > 0) {
            routes.push(...generateRoutesFromMenuItems(item.items));
        }
    }

    return routes;
};

// Generate all routes from menuData automatically
const generateRoutes = (): RouteObject[] => {
    const routes: RouteObject[] = [];

    routes.push({
        path: '/',
        element: <Navigate to="/myshops" replace />
    });

    for (const category of Object.values(MENU_DATA)) {
        if (category.items) {
            routes.push(...generateRoutesFromMenuItems(category.items));
        }
    }

    routes.push({
        path: '*',
        element: <NotFound />
    });

    return routes;
};

// Export the auto-generated routes
export const routes = generateRoutes();
